import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Heading, Text } from '@/Components/ui/typography';
import { PartyPopper, ArrowRight, Megaphone, X } from 'lucide-react';

/**
 * Shown after a job is created.
 *
 * `onBroadcast` (optional) — when supplied, the primary action becomes
 * "Broadcast this job": the job is saved but not visible to candidates until
 * it is published, so that is the natural next step. Callers that cannot
 * broadcast (no job id) omit it and get the original single-button card.
 */
function JobPostingSuccess({ onClose, onBroadcast }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleViewAllJobs = () => {
    onClose();

    // Check if this is an admin creating a job for a company by checking URL
    const params = new URLSearchParams(location.search || '');
    const companyID = params.get('companyID');

    if (companyID) {
      // Admin is posting for a company - redirect to admin's JD table
      navigate(`/all-jobs?companyID=${encodeURIComponent(companyID)}`);
    } else {
      // Regular company user posting their own job
      navigate('/hirewithus/my-jobs');
    }
  };

  return (
    <Card className="relative w-full max-w-[500px] mx-auto">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full hover:bg-muted"
        onClick={onClose}
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </Button>

      <CardHeader className="text-center pb-4 pt-8">
        <div className="flex justify-center mb-4">
          <div className="rounded-full bg-green-500/10 p-3">
            <PartyPopper className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <Heading level="h2" className="text-2xl font-semibold mb-2">
          Congratulations! Your Job is successfully posted!
        </Heading>
        <Text variant="muted" size="base" className="mt-2">
          {onBroadcast
            ? 'It is saved in your dashboard. Broadcast it to open applications and list it on your careers page, Google and more.'
            : 'You can manage your job from the my-jobs section in your dashboard'}
        </Text>
      </CardHeader>

      <CardContent className="pt-0 pb-6">
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          {onBroadcast && (
            <Button onClick={onBroadcast} className="gap-2">
              <Megaphone className="h-4 w-4" />
              <span>Broadcast this job</span>
            </Button>
          )}
          <Button
            onClick={handleViewAllJobs}
            variant="outline"
            className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
          >
            <span>View All Jobs</span>
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default JobPostingSuccess;
